// =====================================================
// REFER & EARN DASHBOARD  (STEP 2 / 9 / 14 / 16 / 17 / 18)
//
// The screen the 🎁 Refer & Earn button on the home page opens. Two tabs:
//
//   My Referrals — the share link, the six counters, and one live progress
//                  card per referred user (STEP 9's exact layout: the three
//                  ticks, the usage clock, the time remaining and the reward).
//   Wallet       — Total Earned / Pending / Available / Withdrawn, the payout
//                  details form, Withdraw, and the withdrawal history.
//
// It renders what the server says and computes nothing of its own. The one
// exception is the countdown, which ticks locally between refreshes purely so
// "17h 18m" does not sit frozen on screen — every value it counts down from,
// and every decision about qualification, comes from the backend.
// =====================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import Footer from "../Footer";
// The three conditions, explained. Same for every user, always shown, and
// rendered on this page only.
import ReferralHowItWorks from "./ReferralHowItWorks";
import "./ReferEarn.css";
import {
  getDashboard,
  getWallet,
  saveBankDetails,
  requestWithdrawal,
  formatClock,
  formatRemaining,
  statusMeta,
  formatDateTime,
} from "../../services/referralApi";
// PART 9 / PART 10 — both money operations on this page (saving payout details
// and requesting a withdrawal) are confirmed by an emailed code first, using
// the app's ONE OTP implementation. This dialog is the same component the Pro
// bank screen uses.
import OtpVerifyModal from "../otp/OtpVerifyModal";
import { OTP_PURPOSE } from "../../services/otpApi";

// How often the dashboard re-reads the server. Progress moves in minutes, not
// seconds, so 30s keeps it live without hammering the API.
const REFRESH_MS = 30 * 1000;

// Cards the user has dismissed with the ✕ on the card. This hides the card in
// this browser only — the referral, its progress and its reward are untouched
// on the server, so nothing about qualification or the wallet changes. Kept in
// localStorage so a dismissal survives both the 30s refresh above and a reload.
const hiddenKey = () => `re-hidden-referrals:${localStorage.getItem("userId") || "guest"}`;

// The same idea for the withdrawal trail: a long payout history makes the page
// scroll forever, so each entry can be hidden from this browser. The withdrawal
// itself — its status, its money — is untouched on the server.
const hiddenWithdrawalsKey = () =>
  `re-hidden-withdrawals:${localStorage.getItem("userId") || "guest"}`;

function readHiddenList(key) {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return Array.isArray(saved) ? saved.map(String) : [];
  } catch {
    return [];
  }
}

function writeHiddenList(key, ids) {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private mode) — the dismissal still holds for this
    // session, which is all the removal has to do.
  }
}

function readHidden() {
  return readHiddenList(hiddenKey());
}

function writeHidden(ids) {
  writeHiddenList(hiddenKey(), ids);
}

function readHiddenWithdrawals() {
  return readHiddenList(hiddenWithdrawalsKey());
}

function writeHiddenWithdrawals(ids) {
  writeHiddenList(hiddenWithdrawalsKey(), ids);
}

function ReferEarn() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("referrals");
  const [toast, setToast] = useState(null);
  const [hiddenIds, setHiddenIds] = useState(readHidden);

  // Ticks once a second so the countdowns move between server refreshes.
  const [tick, setTick] = useState(0);
  const mounted = useRef(true);

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => mounted.current && setToast(null), 3000);
  };

  // Removes one card, by id, and only that card. Every other card — and every
  // counter above, which the server owns — is left exactly as it was.
  const removeReferral = (id) => {
    const key = String(id);
    setHiddenIds((prev) => {
      if (prev.includes(key)) return prev;
      const next = [...prev, key];
      writeHidden(next);
      return next;
    });
    showToast("Referral card removed", "info");
  };

  // Brings every removed card back. Nothing was ever deleted, so this is just
  // the dismissed list being emptied.
  const restoreRemoved = () => {
    setHiddenIds([]);
    writeHidden([]);
    showToast("Removed cards restored", "success");
  };

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await getDashboard();
      if (!mounted.current) return;
      setData(res);
      setError("");
    } catch (err) {
      if (!mounted.current) return;
      if (err.response?.status === 401) {
        navigate("/");
        return;
      }
      setError("Could not load your Refer & Earn dashboard. Pull to refresh.");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    mounted.current = true;
    load();
    const refresh = setInterval(() => load(true), REFRESH_MS);
    const clock = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      mounted.current = false;
      clearInterval(refresh);
      clearInterval(clock);
    };
  }, [load]);

  // ===== STEP 3 — SHARE THE LINK =====
  const shareLink = async () => {
    const link = data?.referral?.referralLink;
    if (!link) return;
    // The usage requirement comes from the server, so the shared message can
    // never drift from the rule that is actually enforced.
    const minutes = Math.round(data.requiredUsageMs / 60000);
    const text = `Join me on TheBlogsSphere! Install the app, allow notifications, complete the steps and use it for ${minutes} minutes — we both win. ${link}`;

    // The native share sheet where the browser has one (every mobile browser),
    // clipboard everywhere else.
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join TheBlogsSphere", text, url: link });
        return;
      } catch {
        // The user dismissed the sheet — fall through to copy rather than
        // leaving them with nothing.
      }
    }
    try {
      await navigator.clipboard.writeText(link);
      showToast("Referral link copied ✅", "success");
    } catch {
      showToast("Could not copy the link. Long-press to copy it manually.", "error");
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.referral.referralCode);
      showToast("Referral code copied ✅", "success");
    } catch {
      showToast("Could not copy the code.", "error");
    }
  };

  if (loading && !data) {
    return (
      <div className="re-wrapper">
        <Navbar />
        <div className="re-container">
          <div className="re-loading">
            <div className="re-spinner" />
            <p>Loading Refer &amp; Earn…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="re-wrapper">
        <Navbar />
        <div className="re-container">
          <div className="re-error">
            <p>{error}</p>
            <button className="re-btn" onClick={() => load()}>Try again</button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const s = data.summary;

  // The cards still on screen, and how many the user has dismissed — the
  // counters above stay on the server's full numbers either way.
  const visibleReferrals = data.referrals.filter((r) => !hiddenIds.includes(String(r.id)));
  const removedCount = data.referrals.length - visibleReferrals.length;

  return (
    <div className="re-wrapper">
      <Navbar />

      <div className="re-container">
        {/* ===== HERO ===== */}
        <header className="re-hero">
          <div className="re-badge">🎁 Refer &amp; Earn</div>
          <h1 className="re-title">
            Earn <span className="re-amount">₹{data.rewardAmount}</span> per friend
          </h1>
          <p className="re-sub">
            They install the app, allow notifications, complete every step below and use it for{" "}
            {Math.round(data.requiredUsageMs / 60000)} minutes within {data.windowHours} hours —
            you earn ₹{data.rewardAmount} once it is verified.
          </p>
        </header>

        {/* ===== HOW IT WORKS (STEP 5) + TERMS =====
            Every active condition with the action that completes it, then the
            programme terms. The same for every user, permanent, and shown on
            this page only. The numbers AND the condition list come from the
            dashboard the page has already loaded — the server's live config. */}
        <ReferralHowItWorks
          rewardAmount={data.rewardAmount}
          usageMinutes={Math.round(data.requiredUsageMs / 60000)}
          windowHours={data.windowHours}
          conditions={data.conditions}
        />

        {/* ===== STEP 3 — MY REFERRAL LINK ===== */}
        <section className="re-card re-link-card">
          <h2 className="re-card-title">My Referral Link</h2>
          <div className="re-link-row">
            <code className="re-link">{data.referral.referralLink}</code>
          </div>
          <div className="re-link-actions">
            <button className="re-btn re-btn-primary" onClick={shareLink}>
              🔗 Share Referral Link
            </button>
            <button className="re-btn re-btn-ghost" onClick={copyCode}>
              Code: <strong>{data.referral.referralCode}</strong>
            </button>
          </div>
        </section>

        {/* ===== STEP 2 — THE COUNTERS ===== */}
        <section className="re-stats">
          <Stat label="Total Referrals" value={s.total} tone="neutral" />
          <Stat label="Completed" value={s.completed} tone="good" />
          <Stat label="Pending" value={s.pending} tone="warn" />
          <Stat label="Expired" value={s.expired} tone="bad" />
          {s.underReview > 0 && <Stat label="Under Review" value={s.underReview} tone="review" />}
          {s.rejected > 0 && <Stat label="Rejected" value={s.rejected} tone="bad" />}
        </section>

        {/* ===== TABS ===== */}
        <nav className="re-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === "referrals"}
            className={`re-tab ${tab === "referrals" ? "is-active" : ""}`}
            onClick={() => setTab("referrals")}
          >
            My Referrals
          </button>
          <button
            role="tab"
            aria-selected={tab === "wallet"}
            className={`re-tab ${tab === "wallet" ? "is-active" : ""}`}
            onClick={() => setTab("wallet")}
          >
            Wallet · ₹{data.wallet.available}
          </button>
        </nav>

        {/* `tick` is not read below — it re-renders this component once a
            second, which is what keeps every card's countdown moving between
            server refreshes. */}
        {tab === "referrals" ? (
          <>
            {/* The way back. Removing a card only hides it, so this puts every
                dismissed card straight back — no console, no data loss. */}
            {removedCount > 0 && (
              <div className="re-restore-bar">
                <span>
                  {removedCount} card{removedCount > 1 ? "s" : ""} hidden — nothing was deleted
                </span>
                <button className="re-btn re-btn-ghost re-btn-sm" onClick={restoreRemoved}>
                  ↺ Show hidden cards
                </button>
              </div>
            )}
            {/* Skipped when every card is hidden: the server does have
                referrals, so "No referrals yet" would be untrue — the restore
                bar above already says what happened. */}
            {(visibleReferrals.length > 0 || removedCount === 0) && (
              <ReferralList
                referrals={visibleReferrals}
                rewardAmount={data.rewardAmount}
                onRemove={removeReferral}
              />
            )}
          </>
        ) : (
          <WalletPanel
            wallet={data.wallet}
            onChanged={() => load(true)}
            showToast={showToast}
          />
        )}
      </div>

      {toast && <div className={`re-toast re-toast-${toast.type}`}>{toast.message}</div>}
      <Footer />
    </div>
  );
}

// ===== ONE COUNTER TILE =====
function Stat({ label, value, tone }) {
  return (
    <div className={`re-stat re-stat-${tone}`}>
      <span className="re-stat-value">{value}</span>
      <span className="re-stat-label">{label}</span>
    </div>
  );
}

// =====================================================
// STEP 9 / STEP 14 — THE PROGRESS CARDS
// =====================================================

function ReferralList({ referrals, rewardAmount, onRemove }) {
  if (!referrals.length) {
    return (
      <section className="re-card re-empty">
        <div className="re-empty-icon">👥</div>
        <h3>No referrals yet</h3>
        <p>
          Share your link above. As soon as someone joins with it, their progress
          through the three conditions appears here — live.
        </p>
      </section>
    );
  }

  return (
    <section className="re-list">
      {referrals.map((r) => (
        <ReferralCard key={r.id} r={r} rewardAmount={rewardAmount} onRemove={onRemove} />
      ))}
    </section>
  );
}

function ReferralCard({ r, rewardAmount, onRemove }) {
  const meta = statusMeta(r.status);
  // The countdown is recomputed from the server's deadline on every tick, so it
  // stays right without the server having to be asked every second.
  const remainingMs = Math.max(0, new Date(r.deadlineAt).getTime() - Date.now());
  const usagePct = Math.min(100, Math.round((r.usage.activeMs / r.usage.requiredMs) * 100));
  const live = r.status === "pending" || r.status === "in_progress";

  return (
    <article className="re-card re-referral">
      <header className="re-referral-head">
        <div>
          <h3 className="re-referral-name">{r.name}</h3>
          <p className="re-referral-dates">
            Joined: {formatDateTime(r.joinedAt)}
            <br />
            Deadline: {formatDateTime(r.deadlineAt)}
          </p>
        </div>
        <div className="re-referral-actions">
          <span className="re-chip" style={{ "--chip": meta.color }}>
            {meta.icon} {meta.label}
          </span>
          {/* Removes this card only. It dismisses the card from the list — the
              referral itself, its progress and its reward are untouched. */}
          <button
            type="button"
            className="re-remove"
            title="Remove this card"
            aria-label={`Remove ${r.name}'s referral card`}
            onClick={() => onRemove(r.id)}
          >
            ✕
          </button>
        </div>
      </header>

      {/* Every active condition, in the order the qualification flow runs, as
          the server's condition engine reports it. An older response without
          `conditions` falls back to the original three. */}
      <ul className="re-conditions">
        {r.conditions ? (
          r.conditions
            .filter((c) => c.type !== "attribution")
            .map((c) => (
              <Condition
                key={c.key}
                done={c.status === "passed"}
                failed={c.status === "failed"}
                label={
                  c.type === "usage"
                    ? `Use the App for ${Math.round(r.usage.requiredMs / 60000)} Minutes`
                    : `${c.name}${c.required ? "" : " (optional)"}`
                }
                at={c.at}
              />
            ))
        ) : (
          <>
            <Condition done={r.install.done} label="Install TheBlogsSphere" at={r.install.at} />
            <Condition done={r.blog.done} label="Publish One Genuine Blog" at={r.blog.at} />
            <Condition
              done={r.usage.done}
              label={`Use the App for ${Math.round(r.usage.requiredMs / 60000)} Minutes`}
              at={r.usage.at}
            />
          </>
        )}
      </ul>

      {/* The usage clock — shown until it is complete, then folded away. */}
      {!r.usage.done && (
        <div className="re-usage">
          <div className="re-usage-row">
            <span>Active Usage</span>
            <strong>
              {formatClock(r.usage.activeMs)} / {formatClock(r.usage.requiredMs)}
            </strong>
          </div>
          <div className="re-progress">
            <div className="re-progress-fill" style={{ width: `${usagePct}%` }} />
          </div>
        </div>
      )}

      <footer className="re-referral-foot">
        <div className="re-foot-item">
          <span className="re-foot-label">Conditions</span>
          <strong>{r.completed}/{r.total} Completed</strong>
        </div>
        {live && (
          <div className="re-foot-item">
            <span className="re-foot-label">Time Remaining</span>
            <strong className={remainingMs < 3600000 ? "re-urgent" : ""}>
              {formatRemaining(remainingMs)}
            </strong>
          </div>
        )}
        <div className="re-foot-item">
          <span className="re-foot-label">Reward</span>
          <strong style={{ color: meta.color }}>
            {r.status === "completed"
              ? `🎉 ₹${r.reward.amount} Earned`
              : r.status === "expired"
              ? "₹0 — Expired"
              : r.status === "under_review"
              ? "Under Review"
              : r.status === "rejected"
              ? "₹0 — Rejected"
              : `₹${rewardAmount} Pending`}
          </strong>
        </div>
      </footer>

      {r.status === "expired" && (
        <p className="re-note re-note-bad">Expired — Conditions Not Completed</p>
      )}
      {r.status === "under_review" && (
        <p className="re-note re-note-warn">
          All conditions are complete. This referral is being verified before
          the reward is credited.
        </p>
      )}
      {/* Conditions were met, but one has since failed (most often:
          notifications turned off). The reward cannot be approved until it
          passes again. */}
      {(r.status === "completed" || r.status === "under_review") &&
        r.eligibility?.status === "ineligible" &&
        r.reward.status === "pending" && (
          <p className="re-note re-note-bad">
            On hold — a required condition is no longer met
            {r.conditions?.some((c) => c.type === "notification" && c.status === "failed")
              ? " (notifications were turned off)"
              : ""}
            . The reward can be approved once it is met again.
          </p>
        )}
    </article>
  );
}

function Condition({ done, failed, label, at }) {
  return (
    <li className={`re-condition ${done ? "is-done" : ""}`}>
      <span className="re-condition-mark">{done ? "✅" : failed ? "❌" : "⏳"}</span>
      <span className="re-condition-label">{label}</span>
      {done && at && <span className="re-condition-at">{formatDateTime(at)}</span>}
    </li>
  );
}

// =====================================================
// STEP 16 / 17 / 18 — WALLET
// =====================================================

function WalletPanel({ wallet, onChanged, showToast }) {
  const [amount, setAmount] = useState(String(wallet.minWithdrawal));
  const [busy, setBusy] = useState(false);
  const [showBank, setShowBank] = useState(false);

  // The dashboard call carries the balances; the withdrawal history and the
  // saved payout details are only needed on this tab, so they are fetched when
  // it opens rather than on every dashboard load.
  const [detail, setDetail] = useState({ withdrawals: [], bank: null });
  const reloadDetail = useCallback(() => {
    getWallet()
      .then((res) => setDetail({ withdrawals: res.withdrawals || [], bank: res.bank || null }))
      .catch(() => {});
  }, []);
  useEffect(reloadDetail, [reloadDetail]);

  // Entries the user has dismissed with the ✕ on the row. Hidden in this
  // browser only — the payout, its status and its amount stay exactly as the
  // server has them, and the ↺ below brings every row straight back.
  const [hiddenWithdrawals, setHiddenWithdrawals] = useState(readHiddenWithdrawals);

  const hideWithdrawal = (id) => {
    const key = String(id);
    setHiddenWithdrawals((prev) => {
      if (prev.includes(key)) return prev;
      const next = [...prev, key];
      writeHiddenWithdrawals(next);
      return next;
    });
    showToast("Entry hidden", "info");
  };

  const restoreWithdrawals = () => {
    setHiddenWithdrawals([]);
    writeHiddenWithdrawals([]);
    showToast("Hidden entries restored", "success");
  };

  const visibleWithdrawals = detail.withdrawals.filter(
    (w) => !hiddenWithdrawals.includes(String(w.id))
  );
  const hiddenWithdrawalCount = detail.withdrawals.length - visibleWithdrawals.length;

  // ── PART 10 — WITHDRAWAL IS CONFIRMED BY EMAIL CODE ──
  // Submitting the form opens the dialog; the withdrawal request itself is made
  // only after the code is accepted, with the ticket it returns. The server
  // spends that ticket BEFORE it touches the wallet, so a refused confirmation
  // cannot move money into `locked` and strand it there.
  const [showWithdrawOtp, setShowWithdrawOtp] = useState(false);

  const withdraw = (e) => {
    e.preventDefault();
    setShowWithdrawOtp(true);
  };

  const withdrawWithTicket = async (otpTicket) => {
    setShowWithdrawOtp(false);
    setBusy(true);
    try {
      const res = await requestWithdrawal(Number(amount), otpTicket);
      showToast(res.message || "Withdrawal requested ✅", "success");
      onChanged();
      reloadDetail();
    } catch (err) {
      showToast(err.response?.data?.message || "Could not request the withdrawal.", "error");
    } finally {
      setBusy(false);
    }
  };

  const canWithdraw = wallet.available >= wallet.minWithdrawal;

  return (
    <>
      <section className="re-stats re-wallet-stats">
        <Stat label="Total Earned" value={`₹${wallet.totalEarned}`} tone="good" />
        <Stat label="Pending Rewards" value={`₹${wallet.pendingRewards}`} tone="warn" />
        <Stat label="Available Balance" value={`₹${wallet.available}`} tone="neutral" />
        <Stat label="Withdrawn" value={`₹${wallet.withdrawn}`} tone="muted" />
      </section>

      {wallet.locked > 0 && (
        <p className="re-note re-note-warn">
          ₹{wallet.locked} is held against a withdrawal that is still being processed.
        </p>
      )}

      <section className="re-card">
        <h2 className="re-card-title">Withdraw</h2>
        <form className="re-withdraw" onSubmit={withdraw}>
          <label htmlFor="re-amount">Amount (₹)</label>
          <input
            id="re-amount"
            type="number"
            min={wallet.minWithdrawal}
            max={wallet.available}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={!canWithdraw || busy}
          />
          <button
            className="re-btn re-btn-primary"
            type="submit"
            disabled={!canWithdraw || busy}
          >
            {busy ? "Requesting…" : "Request Withdrawal"}
          </button>
        </form>
        <p className="re-hint">
          Minimum withdrawal ₹{wallet.minWithdrawal}.{" "}
          {!canWithdraw && `You have ₹${wallet.available} available. `}
          We'll email you a 6-digit code to confirm the request.
        </p>
      </section>

      {/* PART 10 — the withdrawal confirmation. Same dialog, same OTP system
          as the bank form below; only the purpose differs. */}
      <OtpVerifyModal
        open={showWithdrawOtp}
        purpose={OTP_PURPOSE.REWARD_WITHDRAW}
        title="Confirm your withdrawal"
        lead={`We'll confirm this ₹${amount} withdrawal with a code sent to your account's email.`}
        confirmLabel="Confirm withdrawal"
        onCancel={() => setShowWithdrawOtp(false)}
        onVerified={withdrawWithTicket}
      />

      <section className="re-card">
        <div className="re-card-head">
          <h2 className="re-card-title">Bank Details</h2>
          <button className="re-btn re-btn-ghost" onClick={() => setShowBank((v) => !v)}>
            {showBank ? "Close" : detail.bank ? "Edit" : "Add"}
          </button>
        </div>

        {showBank ? (
          <BankForm
            existing={detail.bank}
            onSaved={() => {
              setShowBank(false);
              onChanged();
              reloadDetail();
              showToast("Payout details saved ✅", "success");
            }}
            onError={(m) => showToast(m, "error")}
          />
        ) : detail.bank ? (
          <dl className="re-bank-view">
            <div><dt>Account Holder</dt><dd>{detail.bank.accountHolderName || "—"}</dd></div>
            <div><dt>Bank</dt><dd>{detail.bank.bankName || "—"}</dd></div>
            {/* The owner only ever sees the mask — the full number lives
                encrypted on the server and is shown only on the admin payout
                screen, where a transfer is actually made. */}
            <div><dt>Account</dt><dd>{detail.bank.accountNumberMasked || "—"}</dd></div>
            <div><dt>IFSC</dt><dd>{detail.bank.ifsc || "—"}</dd></div>
            {/* The contact number, in place of the UPI field. It is not a payout
                destination — it is how the admin reaches you about a payout. */}
            <div><dt>Phone</dt><dd>{detail.bank.phone || "—"}</dd></div>
            {/* Only for accounts saved before the UPI field was replaced. */}
            {detail.bank.upiId && (
              <div><dt>UPI ID (legacy)</dt><dd>{detail.bank.upiId}</dd></div>
            )}
            <div>
              <dt>Verification</dt>
              <dd className={`re-verify re-verify-${detail.bank.verificationStatus}`}>
                {detail.bank.verificationStatus}
              </dd>
            </div>
            {/* PART 9 — whether these details were confirmed by an emailed
                code. Distinct from the admin's eyeball check above, and it is
                what a withdrawal actually requires. Rows saved before this
                feature existed show as not confirmed; re-saving them (which
                asks for a code) is all it takes. */}
            <div>
              <dt>Email confirmed</dt>
              <dd className={`re-verify re-verify-${detail.bank.otpVerified ? "verified" : "unverified"}`}>
                {detail.bank.otpVerified
                  ? `yes — ${formatDateTime(detail.bank.otpVerifiedAt)}`
                  : "not yet — press Edit and save to confirm"}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="re-hint">
            Add your bank account to withdraw your earnings. We'll email you a 6-digit
            code to confirm it. We never ask for — and never store — your UPI PIN, bank
            password or any banking OTP.
          </p>
        )}
      </section>

      {/* STEP 17 — the withdrawal trail: Pending → Processing → Paid. */}
      {detail.withdrawals.length > 0 && (
        <section className="re-card">
          <h2 className="re-card-title">Withdrawal History</h2>
          {/* The way back. Hiding a row only hides it, so this puts every
              dismissed entry straight back — no console, no data loss. */}
          {hiddenWithdrawalCount > 0 && (
            <div className="re-restore-bar">
              <span>
                {hiddenWithdrawalCount} entr{hiddenWithdrawalCount > 1 ? "ies" : "y"} hidden —
                nothing was deleted
              </span>
              <button className="re-btn re-btn-ghost re-btn-sm" onClick={restoreWithdrawals}>
                ↺ Show hidden entries
              </button>
            </div>
          )}
          <ul className="re-withdrawals">
            {visibleWithdrawals.map((w) => (
              <li key={w.id} className={`re-withdrawal re-w-${w.status}`}>
                <div className="re-withdrawal-main">
                  <strong>₹{w.amount}</strong>
                  <span className="re-withdrawal-actions">
                    <span className="re-withdrawal-status">{w.status}</span>
                    {/* Hides this entry only. The payout itself is untouched. */}
                    <button
                      type="button"
                      className="re-remove"
                      title="Hide this entry"
                      aria-label={`Hide the ₹${w.amount} withdrawal entry`}
                      onClick={() => hideWithdrawal(w.id)}
                    >
                      ✕
                    </button>
                  </span>
                </div>
                <div className="re-withdrawal-meta">
                  <span>{formatDateTime(w.requestedAt)}</span>
                  {w.utr && <span>UTR: {w.utr}</span>}
                  {w.status === "rejected" && w.reason && (
                    <span className="re-withdrawal-reason">{w.reason}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

// STEP 18 — the payout details form.
//
// It collects exactly five fields. There is deliberately no input for a UPI
// PIN, a bank password or an OTP: the server drops any such field and has
// nowhere to store one, and asking for them here is how users get phished.
//
// TASK 3 — the UPI ID field is gone; a PHONE NUMBER takes its place. The money
// always goes to the bank account; the number is how the admin reaches you if a
// payout has a problem.
function BankForm({ existing, onSaved, onError }) {
  const [form, setForm] = useState({
    accountHolderName: existing?.accountHolderName || "",
    bankName: existing?.bankName || "",
    // Deliberately blank even when an account is on file: the client is never
    // given the full number back, so there is nothing to prefill. Leaving it
    // empty keeps the saved account unchanged (the server only overwrites when
    // a new number is actually supplied).
    accountNumber: "",
    ifsc: existing?.ifsc || "",
    phone: existing?.phone || "",
  });
  const [busy, setBusy] = useState(false);
  const [showOtp, setShowOtp] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // ── PART 9 — OTP FIRST, ON BOTH PATHS ──
  //   Add Bank Details  → Email OTP → Verify OTP → Save
  //   Edit Bank Details → Email OTP → Verify OTP → Allow edit → Save
  //
  // Submitting does NOT save: it opens the confirmation dialog. Which purpose
  // the server will require depends on whether details already exist, and the
  // server decides that from the database — a code issued for "add" cannot
  // authorise a change to an existing payout destination.
  const purpose = existing ? OTP_PURPOSE.BANK_EDIT : OTP_PURPOSE.BANK_ADD;

  const submit = (e) => {
    e.preventDefault();
    setShowOtp(true);
  };

  const saveWithTicket = async (otpTicket) => {
    setShowOtp(false);
    setBusy(true);
    try {
      await saveBankDetails({ ...form, otpTicket });
      onSaved();
    } catch (err) {
      onError(err.response?.data?.message || "Could not save your payout details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="re-bank" onSubmit={submit}>
      <label>
        Account Holder Name
        <input value={form.accountHolderName} onChange={set("accountHolderName")} required maxLength={100} />
      </label>
      <label>
        Bank Name
        <input value={form.bankName} onChange={set("bankName")} maxLength={100} />
      </label>
      <label>
        Account Number
        {existing?.accountNumberMasked && (
          <span className="re-optional"> (currently {existing.accountNumberMasked} — leave blank to keep it)</span>
        )}
        <input
          value={form.accountNumber}
          onChange={set("accountNumber")}
          inputMode="numeric"
          maxLength={34}
          autoComplete="off"
        />
      </label>
      <label>
        IFSC
        <input value={form.ifsc} onChange={set("ifsc")} maxLength={20} autoComplete="off" />
      </label>
      <label>
        Phone Number <span className="re-optional">(so we can contact you about a payout)</span>
        <input
          value={form.phone}
          onChange={set("phone")}
          type="tel"
          inputMode="tel"
          maxLength={20}
          required
          autoComplete="tel"
        />
      </label>
      <p className="re-hint">
        📧 When you continue we'll email a 6-digit code to your account's address.
        Nothing is saved until you enter it, so{" "}
        {existing ? "your current details stay as they are" : "nothing is stored"} if you
        stop here.
      </p>
      <p className="re-hint re-hint-strong">
        🔒 Never share your UPI PIN, bank password or a BANKING OTP — with us or anyone
        else. We will never ask for them. The only code we send is the 6-digit one that
        confirms this form, and we will never ask you to read it out to anyone.
      </p>
      <button className="re-btn re-btn-primary" type="submit" disabled={busy}>
        {busy ? "Saving…" : "Continue — verify by email"}
      </button>

      <OtpVerifyModal
        open={showOtp}
        purpose={purpose}
        title={existing ? "Confirm this payout change" : "Confirm your payout details"}
        lead="This is where your earnings will be sent, so we confirm it by email first."
        confirmLabel="Verify & save"
        onCancel={() => setShowOtp(false)}
        onVerified={saveWithTicket}
      />
    </form>
  );
}

export default ReferEarn;
